import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useAuthStore } from '../store/authStore'

const changePasswordSchema = z
  .object({
    currentPassword: z.string().min(1, 'Current password is required'),
    newPassword: z
      .string()
      .min(8, 'Password must be at least 8 characters')
      .regex(/[A-Za-z]/, 'Password must include at least one letter')
      .regex(/\d/, 'Password must include at least one number'),
    confirmPassword: z.string().min(1, 'Please confirm your password'),
  })
  .refine((data) => data.newPassword === data.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
  })
  .refine((data) => data.currentPassword !== data.newPassword, {
    message: 'New password must be different from current password',
    path: ['newPassword'],
  })

type ChangePasswordFormData = z.infer<typeof changePasswordSchema>

export function ChangePasswordPage(): JSX.Element {
  const changePassword = useAuthStore((s) => s.changePassword)
  const navigate = useNavigate()
  const [serverError, setServerError] = useState('')
  const [successMessage, setSuccessMessage] = useState('')

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<ChangePasswordFormData>({
    resolver: zodResolver(changePasswordSchema),
    defaultValues: { currentPassword: '', newPassword: '', confirmPassword: '' },
  })

  const onSubmit = async (formData: ChangePasswordFormData): Promise<void> => {
    setServerError('')
    setSuccessMessage('')
    const result = await changePassword(formData.currentPassword, formData.newPassword)

    if (!result.success) {
      setServerError(result.error || 'Unable to change password.')
      return
    }

    setSuccessMessage('Password changed successfully. Redirecting to login...')
    setTimeout(() => {
      navigate('/login', { replace: true })
    }, 1200)
  }

  return (
    <div className="mx-auto w-full max-w-xl p-6">
      <div className="rounded-xl border border-[#E5E7EB] bg-white p-8 shadow-sm">
        <h1 className="mb-1 text-[28px] font-bold leading-tight text-gray-900">
          Change password
        </h1>
        <p className="mb-6 text-[14px] text-gray-500">
          For security, you will be signed out after changing your password.
        </p>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
          <div>
            <label
              htmlFor="currentPassword"
              className="mb-1.5 block text-[13px] font-medium text-gray-700"
            >
              Current Password
            </label>
            <input
              id="currentPassword"
              type="password"
              {...register('currentPassword')}
              className="h-10 w-full rounded-lg border border-[#E5E7EB] bg-white px-4 text-[14px] text-gray-900 outline-none transition-colors focus:border-brand-green"
            />
            {errors.currentPassword && (
              <p className="mt-1 text-xs text-red-600">{errors.currentPassword.message}</p>
            )}
          </div>

          <div>
            <label htmlFor="newPassword" className="mb-1.5 block text-[13px] font-medium text-gray-700">
              New Password
            </label>
            <input
              id="newPassword"
              type="password"
              {...register('newPassword')}
              className="h-10 w-full rounded-lg border border-[#E5E7EB] bg-white px-4 text-[14px] text-gray-900 outline-none transition-colors focus:border-brand-green"
            />
            {errors.newPassword && (
              <p className="mt-1 text-xs text-red-600">{errors.newPassword.message}</p>
            )}
          </div>

          <div>
            <label
              htmlFor="confirmPassword"
              className="mb-1.5 block text-[13px] font-medium text-gray-700"
            >
              Confirm New Password
            </label>
            <input
              id="confirmPassword"
              type="password"
              {...register('confirmPassword')}
              className="h-10 w-full rounded-lg border border-[#E5E7EB] bg-white px-4 text-[14px] text-gray-900 outline-none transition-colors focus:border-brand-green"
            />
            {errors.confirmPassword && (
              <p className="mt-1 text-xs text-red-600">{errors.confirmPassword.message}</p>
            )}
          </div>

          {successMessage && (
            <div className="rounded-lg border border-green-200 bg-green-50 p-3">
              <p className="text-[13px] text-green-700">{successMessage}</p>
            </div>
          )}

          {serverError && (
            <div className="rounded-lg border border-red-200 bg-red-50 p-3">
              <p className="text-[13px] text-red-600">{serverError}</p>
            </div>
          )}

          <button
            type="submit"
            disabled={isSubmitting}
            className="h-10 w-full rounded-lg bg-brand-green text-[14px] font-semibold text-black transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {isSubmitting ? 'Saving...' : 'Update password'}
          </button>
        </form>
      </div>
    </div>
  )
}
